import { AsyncPipe, NgIf } from '@angular/common';
import { HttpParams } from '@angular/common/http';
import { Component, CUSTOM_ELEMENTS_SCHEMA, inject, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { getV2UiBaseUrl, LF_MASTER_LOGO_URL, V2_UI_REDIRECT_PROJECTS } from '@app/shared/constants';
import { ProjectInfrastructureService } from '@app/shared/services/project-infrastructure.service';
import { UserService } from '@app/shared/services/user.service';
import { User, ZoomMeeting } from 'lfx-pcc';
import { DateTime } from 'luxon';
import { MessageModule } from 'primeng/message';
import { catchError, combineLatest, map, NEVER, Observable, of, switchMap, take } from 'rxjs';

import { MeetingFormComponent } from './components/meeting-form/meeting-form.component';
import { MeetingHeaderComponent } from './components/meeting-header/meeting-header.component';

@Component({
  selector: 'lfx-join-meeting',
  templateUrl: './join-meeting.component.html',
  styleUrls: ['./join-meeting.component.scss'],
  standalone: true,
  imports: [NgIf, MeetingHeaderComponent, MeetingFormComponent, AsyncPipe, MessageModule],
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
})
export class JoinMeetingComponent implements OnInit {
  public meeting$: Observable<ZoomMeeting | null>;
  public user$: Observable<User | null | undefined>;
  public password: string = '';
  public logoURL: string = LF_MASTER_LOGO_URL;

  private activatedRoute: ActivatedRoute = inject(ActivatedRoute);
  private pisService: ProjectInfrastructureService = inject(ProjectInfrastructureService);
  private router: Router = inject(Router);
  private userService: UserService = inject(UserService);

  public ngOnInit(): void {
    this.meeting$ = combineLatest([this.activatedRoute.params, this.activatedRoute.queryParams]).pipe(
      switchMap(([params, queryParams]) => {
        if (queryParams.error && queryParams.error === 'auth0') {
          this.userService.skipAuth0.set(true);
        }

        if (!queryParams.password) {
          this.router.navigate(['/password-required']);
          return of(null);
        }

        const cleanPassword = queryParams.password.replace(/[^0-9a-f-]/gi, '');
        const query = new HttpParams({ fromObject: { password: cleanPassword } });
        this.password = cleanPassword;

        return this.pisService.getMeeting(params.id, query).pipe(
          take(1),
          catchError((error) => {
            console.info('Error', error.message);
            console.info('Error status', error.status);
            if (error.status > 499) {
              this.router.navigate(['/service-unavailable']);
            } else if (error.status === 401) {
              this.router.navigate(['/not-authorized']);
            } else if (error.status === 404) {
              this.router.navigate(['/not-found']);
            } else {
              this.router.navigate(['.'], {
                queryParams: { password: cleanPassword, error: 'auth0' },
                relativeTo: this.activatedRoute,
              });
            }

            return of(null);
          }),
          switchMap((meeting) => {
            // Redirect meetings for specific project slugs to the LFX V2 UI meeting page.
            // Return NEVER so the AsyncPipe stays in the loading state while the browser
            // navigates away — prevents a flash of the old meeting UI before redirect.
            if (meeting?.project && V2_UI_REDIRECT_PROJECTS.includes(meeting.project)) {
              window.location.replace(`${getV2UiBaseUrl()}/meetings/${meeting.id}?password=${cleanPassword}`);
              return NEVER;
            }
            return of(meeting);
          }),
          map((meeting) => {
            if (meeting) {
              // Check if meeting is a recurring meeting and if so, check if the first occurrence is in the past
              // If so, then we need to get the next occurrence
              if (meeting.occurrences && meeting.occurrences.length > 0) {
                // Filter out cancelled occurrences
                meeting.occurrences = meeting.occurrences.filter((occurrence) => occurrence.status !== 'cancel');

                if (meeting.occurrences.length === 0) {
                  return meeting;
                }
                const now = new Date();
                let firstOccurrence = meeting.occurrences[0];
                let meetingDate = DateTime.fromISO(firstOccurrence.start_time)
                  .plus({ minutes: meeting.duration + 40 })
                  .toJSDate();

                while (meeting.occurrences.length > 0 && meetingDate < now) {
                  console.info('Meeting is in the past, getting next occurrence', meetingDate);
                  // Pop the first occurrence off the array
                  meeting.occurrences.shift();

                  if (meeting.occurrences.length === 0) {
                    break;
                  }

                  firstOccurrence = meeting.occurrences[0];
                  meetingDate = DateTime.fromISO(firstOccurrence.start_time)
                    .plus({ minutes: meeting.duration + 40 })
                    .toJSDate();
                }

                // Update meeting start_time to match the current first occurrence
                if (meeting.occurrences.length > 0) {
                  meeting.start_time = meeting.occurrences[0].start_time;
                }
              }
            }

            return meeting;
          })
        );
      })
    );
  }
}
